import {
  isFieldScored,
  isQuestionScored,
  isQuestionVisible,
  isSectionVisible,
  questionWeight,
  type AnswerMap,
  type Question,
  type SampleEntryValue,
  type TemplateDefinition,
  type YesNoNa,
} from './template';
import type { TemplateSettings } from './validateTemplate';

/**
 * Scoring engine (spec §4). Runs client-side at submission and recomputes
 * on post-submission edits.
 *
 * Rules:
 * - N/A answers are excluded from numerator AND denominator.
 * - Hidden sections/questions (gate unanswered or answered the other way)
 *   are excluded entirely, like N/A.
 * - Unanswered scored questions are excluded (submission validation is
 *   responsible for requiring answers; scoring never guesses).
 * - simple: Yes ÷ (Yes + No), i.e. weighted with every weight forced to 1.
 * - weighted: Σ(weight of passed) ÷ Σ(weight of applicable).
 * - weighted_critical: weighted, plus any failed critical question triggers
 *   CriticalBehavior — auto_fail ⇒ overall score 0; cap ⇒ min(score, cap).
 *   Critical failure affects the OVERALL score only; section scores report
 *   the uncapped computation.
 * - Sample groups: each scored sub-field of each entry is an independent
 *   item at the group's weight. A critical sample group fails if any item
 *   in it fails.
 * - Scores are 0–100. A scope with no applicable items scores null.
 */

export interface ScoreBreakdown {
  /** 0–100 after critical behavior, or null if nothing was applicable. */
  score: number | null;
  /** Score before critical behavior was applied. */
  rawScore: number | null;
  criticalFailed: boolean;
  /** Section id → 0–100 or null (uncapped). */
  sectionScores: Record<string, number | null>;
  passedWeight: number;
  applicableWeight: number;
}

interface Tally {
  passed: number;
  applicable: number;
}

function tallyAnswer(
  value: YesNoNa | undefined,
  weight: number,
  tally: Tally,
): 'passed' | 'failed' | 'excluded' {
  if (value !== 'yes' && value !== 'no') return 'excluded'; // na / unanswered
  tally.applicable += weight;
  if (value === 'yes') {
    tally.passed += weight;
    return 'passed';
  }
  return 'failed';
}

function tallyQuestion(
  q: Question,
  answers: AnswerMap,
  weightOverride: number | undefined,
  tally: Tally,
): boolean {
  const weight = weightOverride ?? questionWeight(q);
  let anyFailed = false;

  if (q.type === 'sample_group') {
    const entries = (answers[q.id] ?? []) as SampleEntryValue[];
    if (!Array.isArray(entries)) return false;
    for (const entry of entries) {
      if (!entry) continue;
      for (const field of q.fields ?? []) {
        if (!isFieldScored(field)) continue;
        const outcome = tallyAnswer(
          entry[field.id] as YesNoNa | undefined,
          weight,
          tally,
        );
        if (outcome === 'failed') anyFailed = true;
      }
    }
    return anyFailed;
  }

  const outcome = tallyAnswer(
    answers[q.id] as YesNoNa | undefined,
    weight,
    tally,
  );
  return outcome === 'failed';
}

function toScore(tally: Tally): number | null {
  if (tally.applicable === 0) return null;
  return (tally.passed / tally.applicable) * 100;
}

export function scoreAudit(
  def: TemplateDefinition,
  answers: AnswerMap,
  settings: TemplateSettings,
): ScoreBreakdown {
  // A no-score form never has a score, whatever its questions say.
  if (settings.scoringMode === 'none') {
    return {
      score: null,
      rawScore: null,
      sectionScores: Object.fromEntries(def.sections.map((s) => [s.id, null])),
      criticalFailed: false,
      passedWeight: 0,
      applicableWeight: 0,
    };
  }
  const simple = settings.scoringMode === 'simple';
  const overall: Tally = { passed: 0, applicable: 0 };
  const sectionScores: Record<string, number | null> = {};
  let criticalFailed = false;

  for (const section of def.sections) {
    if (!isSectionVisible(def, section, answers)) {
      sectionScores[section.id] = null;
      continue;
    }
    const sectionTally: Tally = { passed: 0, applicable: 0 };
    for (const q of section.questions) {
      if (!isQuestionScored(q)) continue;
      if (!isQuestionVisible(def, section, q, answers)) continue;
      const failed = tallyQuestion(
        q,
        answers,
        simple ? 1 : undefined,
        sectionTally,
      );
      if (failed && q.critical) criticalFailed = true;
    }
    overall.passed += sectionTally.passed;
    overall.applicable += sectionTally.applicable;
    sectionScores[section.id] = toScore(sectionTally);
  }

  const rawScore = toScore(overall);
  let score = rawScore;

  if (
    settings.scoringMode === 'weighted_critical' &&
    criticalFailed &&
    score !== null
  ) {
    if (settings.criticalBehavior === 'auto_fail') {
      score = 0;
    } else if (settings.criticalBehavior === 'cap') {
      score = Math.min(score, settings.criticalCap ?? 0);
    }
  }
  // Critical flags outside weighted_critical mode carry no score effect.
  if (settings.scoringMode !== 'weighted_critical') criticalFailed = false;

  return {
    score,
    rawScore,
    criticalFailed,
    sectionScores,
    passedWeight: overall.passed,
    applicableWeight: overall.applicable,
  };
}
